import { buildAdaptivePracticePassage } from "./content";
import { masteryBand } from "./mastery";
import type {
  LearningRecommendation,
  MasteryBand,
  MasteryOpportunity,
  MasterySnapshot,
  SkillMastery,
  SkillType,
} from "./types";

const MINIMUM_RECOMMENDATION_ATTEMPTS: Partial<Record<SkillType, number>> = {
  key: 6,
  shiftedKey: 6,
  bigram: 4,
  trigram: 3,
  punctuation: 6,
  number: 6,
};

const SKILL_PRIORITY_WEIGHT: Partial<Record<SkillType, number>> = {
  key: 1,
  shiftedKey: 1.05,
  bigram: 1.08,
  trigram: 1.12,
  punctuation: 1.04,
  number: 1.02,
};

function round(value: number, decimals = 2): number {
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function skillToken(skillId: string): string {
  const separator = skillId.indexOf(":");
  return separator < 0 ? skillId : skillId.slice(separator + 1);
}

export function formatSkillLabel(skillId: string): string {
  const token = skillToken(skillId);
  return token
    .replaceAll("space", "␠")
    .replaceAll(" ", "␠")
    .replaceAll("\t", "⇥");
}

function priorityFor(item: SkillMastery): number {
  const accuracyPenalty = item.accuracy === null ? 0 : 100 - item.accuracy;
  const latencyPenalty =
    item.meanLatencyMs === null ? 0 : Math.min(25, item.meanLatencyMs / 40);
  const need = 100 - item.masteryScore;
  const confidenceWeight = 0.55 + item.confidence * 0.45;
  const typeWeight = SKILL_PRIORITY_WEIGHT[item.skillType] ?? 0.9;
  return round(
    (need * 0.72 + accuracyPenalty * 0.22 + latencyPenalty * 0.06) *
      confidenceWeight *
      typeWeight,
  );
}

function opportunityFromMastery(
  item: SkillMastery,
  kind: "opportunity" | "strength" = "opportunity",
): MasteryOpportunity {
  const label = formatSkillLabel(item.skillId);
  const band = masteryBand(item.masteryScore).toLowerCase();
  return {
    skillId: item.skillId,
    skillType: item.skillType,
    label,
    masteryScore: item.masteryScore,
    confidence: item.confidence,
    attempts: item.attempts,
    sessionCount: item.sessionCount,
    accuracy: item.accuracy,
    priority: priorityFor(item),
    reason:
      kind === "strength"
        ? `${label} is in the ${band} band across ${item.sessionCount} session${item.sessionCount === 1 ? "" : "s"}.`
        : `${label} is in the ${band} band with ${item.attempts} observed attempts.`,
  };
}

function recommendationEligible(item: SkillMastery): boolean {
  const minimum = MINIMUM_RECOMMENDATION_ATTEMPTS[item.skillType];
  const token = skillToken(item.skillId);
  const isWhitespacePattern =
    (item.skillType === "bigram" || item.skillType === "trigram") &&
    /\s/.test(token);
  return (
    minimum !== undefined &&
    item.attempts >= minimum &&
    item.confidence >= 0.25 &&
    item.masteryScore < 70 &&
    item.skillId !== "key:space" &&
    !isWhitespacePattern
  );
}

export function rankMasteryOpportunities(
  mastery: readonly SkillMastery[],
  limit = 8,
): MasteryOpportunity[] {
  return mastery
    .filter(recommendationEligible)
    .map((item) => opportunityFromMastery(item))
    .sort(
      (left, right) =>
        right.priority - left.priority ||
        left.masteryScore - right.masteryScore ||
        right.confidence - left.confidence ||
        left.skillId.localeCompare(right.skillId),
    )
    .slice(0, Math.max(0, limit));
}

export function summarizeMastery(
  mastery: readonly SkillMastery[],
): MasterySnapshot {
  const bands: Record<MasteryBand, number> = {
    New: 0,
    Developing: 0,
    Functional: 0,
    Strong: 0,
    Mastered: 0,
  };
  const byType: MasterySnapshot["byType"] = {};
  let evidenceAttempts = 0;
  let weightedScore = 0;
  let totalWeight = 0;

  for (const item of mastery) {
    bands[masteryBand(item.masteryScore)] += 1;
    byType[item.skillType] = (byType[item.skillType] ?? 0) + 1;
    evidenceAttempts += item.attempts;
    const weight = Math.max(0.05, item.confidence);
    weightedScore += item.masteryScore * weight;
    totalWeight += weight;
  }

  const reliable = mastery.filter((item) => item.confidence >= 0.5);
  const strengths = reliable
    .filter((item) => item.masteryScore >= 70)
    .map((item) => opportunityFromMastery(item, "strength"))
    .sort(
      (left, right) =>
        right.masteryScore - left.masteryScore ||
        right.confidence - left.confidence,
    )
    .slice(0, 6);

  return {
    totalSkills: mastery.length,
    reliableSkills: reliable.length,
    evidenceAttempts,
    overallScore: totalWeight === 0 ? null : round(weightedScore / totalWeight),
    overallConfidence:
      mastery.length === 0
        ? 0
        : round(
            mastery.reduce((sum, item) => sum + item.confidence, 0) /
              mastery.length,
          ),
    bands,
    byType,
    opportunities: rankMasteryOpportunities(mastery),
    strengths,
  };
}

function confidenceLabel(
  confidence: number,
): LearningRecommendation["confidenceLabel"] {
  if (confidence < 0.45) return "Early signal";
  if (confidence < 0.72) return "Growing evidence";
  return "Reliable evidence";
}

function titleFor(primary: MasteryOpportunity): string {
  if (
    primary.skillType === "key" ||
    primary.skillType === "shiftedKey" ||
    primary.skillType === "punctuation" ||
    primary.skillType === "number"
  ) {
    return `Strengthen “${primary.label}” with focused practice`;
  }
  return `Build fluency on the “${primary.label}” pattern`;
}

export function buildMasteryRecommendation(
  mastery: readonly SkillMastery[],
  options: {
    totalPieces?: number;
    recentContentIds?: readonly string[];
  } = {},
): LearningRecommendation | null {
  const opportunities = rankMasteryOpportunities(mastery, 12);
  const primary = opportunities[0];
  if (!primary) {
    return null;
  }

  const targetType =
    primary.skillType === "key" ||
    primary.skillType === "shiftedKey" ||
    primary.skillType === "punctuation" ||
    primary.skillType === "number"
      ? "key"
      : "pattern";
  const targetSkills = opportunities
    .filter((item) =>
      targetType === "key"
        ? ["key", "shiftedKey", "punctuation", "number"].includes(item.skillType)
        : ["bigram", "trigram"].includes(item.skillType),
    )
    .filter((item) => item.masteryScore <= primary.masteryScore + 15)
    .slice(0, 3);
  const strengths = summarizeMastery(mastery).strengths
    .filter((item) => !targetSkills.some((target) => target.skillId === item.skillId))
    .slice(0, 2);
  const targetSkillIds = targetSkills.map((item) => item.skillId);
  const reviewSkillIds = strengths.map((item) => item.skillId);
  const passage = buildAdaptivePracticePassage({
    targetSkillIds,
    reviewSkillIds,
    totalPieces: options.totalPieces ?? 8,
    recentContentIds: options.recentContentIds,
  });
  const confidence = round(
    targetSkills.reduce((sum, item) => sum + item.confidence, 0) /
      targetSkills.length,
  );
  const averageAccuracy =
    targetSkills.reduce((sum, item) => sum + (item.accuracy ?? 0), 0) /
    targetSkills.length;
  const targetLabels = targetSkills.map((item) => `“${item.label}”`);
  const idSeed = targetSkillIds.join("|");
  const idHash = [...idSeed]
    .reduce((hash, character) => (hash * 31 + character.charCodeAt(0)) >>> 0, 11)
    .toString(36);

  return {
    id: `mastery-rec-${idHash}`,
    title: titleFor(primary),
    description:
      `${targetLabels.join(", ")} ${targetLabels.length === 1 ? "is" : "are"} ` +
      `the highest-value ${targetType} ${targetLabels.length === 1 ? "target" : "targets"} ` +
      `supported by your current typing evidence.`,
    actionLabel: "Start prescribed practice",
    mode: "pro",
    source: "mastery-engine",
    chip: `${confidenceLabel(confidence)} · ${targetSkills.length} target${targetSkills.length === 1 ? "" : "s"}`,
    confidence,
    confidenceLabel: confidenceLabel(confidence),
    targetSkillIds,
    rationale: [
      `${primary.label} has the highest current opportunity score (${Math.round(primary.priority)}).`,
      `The prescription puts target work first, then mixes in comfortable support and review.`,
      `The recommendation will update as valid evidence accumulates across more sessions and contexts.`,
    ],
    evidence: [
      {
        label: "Mastery",
        value: `${Math.round(primary.masteryScore)}/100`,
        detail: `${masteryBand(primary.masteryScore)} band`,
      },
      {
        label: "Initial accuracy",
        value: `${Math.round(averageAccuracy)}%`,
        detail: `${targetSkills.reduce((sum, item) => sum + item.attempts, 0)} target attempts`,
      },
      {
        label: "Confidence",
        value: `${Math.round(confidence * 100)}%`,
        detail: confidenceLabel(confidence),
      },
      {
        label: "Exposure",
        value: `${passage.targetExposureCount}`,
        detail: "target appearances in this prescription",
      },
    ],
    preset: {
      id: `preset-${idHash}`,
      activityType: "adaptive",
      practiceType: "specialized",
      difficulty: "medium",
      generalFocus: "general",
      skillFocus: "patterns",
      length: "long",
      contentId: passage.id,
      customText: passage.text,
      targetSkillIds,
      reviewSkillIds,
      recommendationConfidence: confidence,
      confidenceLabel: confidenceLabel(confidence),
    },
  };
}
